import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import { faRotate } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";

export interface RescrapeActionsProps {
	/** True once a file has been delivered and no re-scrape is already on its way. */
	canRequestRescrape: boolean;
	isRescrapePending: boolean;
	isCancelling: boolean;
	handleRequestRescrape?: () => void;
	handleCancelRescrape?: () => void;
}

/**
 * Ordering another scrape of the source, from inside the request it repeats (PREC-3091).
 *
 * While a request is open the button is replaced by why it is gone plus a way to withdraw it —
 * hiding the button on its own just reads as the feature disappearing.
 */
export const RescrapeActions: FunctionComponent<RescrapeActionsProps> = ({
	canRequestRescrape,
	isRescrapePending,
	isCancelling,
	handleRequestRescrape,
	handleCancelRescrape,
}) => {
	const { t } = useTranslation();

	if (isRescrapePending) {
		return (
			<div className="mb-4 flex flex-row items-center gap-3 rounded-xl bg-status-running-light px-4 py-3">
				<span className="grow text-xs text-status-running">
					{t("customEvent.rescrape.pending")}
				</span>
				{handleCancelRescrape && (
					<div className="w-auto shrink-0">
						<Button
							color={ButtonColor.DANGER_SECONDARY}
							isLoading={isCancelling}
							size={ButtonSize.XS}
							title={t("customEvent.rescrape.cancel")}
							onClick={handleCancelRescrape}
						/>
					</div>
				)}
			</div>
		);
	}

	if (!canRequestRescrape || !handleRequestRescrape) {
		return null;
	}

	return (
		<div className="mb-4 flex w-full">
			{/* The shared Button is w-fit; `grow` is what makes it fill the card. */}
			<Button
				className="grow"
				color={ButtonColor.ACTION_SECONDARY}
				image={<Icon className="w-[14px] h-auto mr-2" icon={faRotate} />}
				title={t("customEvent.rescrape.button")}
				onClick={handleRequestRescrape}
			/>
		</div>
	);
};
